import { FC, useEffect, useState, useMemo } from "react";
import { ApiConstants } from "../../../main/constants/ApiConstants";
import IUMB from "../../../main/interfaces/IUMB";
import IArea from "../../../main/interfaces/IArea";
import ISwitchRequest from "../../../main/interfaces/IRequest";

import React from "react";
import BasicCli from "../../../main/components/basicCli/BasicCli";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import GeneralModal from "../../../main/components/modal/general/GeneralModal";
import useGetUser from "../../../main/hooks/useGetUser";
import axiosInstance from "../../../main/initializers/axios";
import { toast } from "react-toastify";
import GeneralInput from "../../../main/components/generalInput/GeneralInput";
import { useForm } from "react-hook-form";
import "./Umbs.css";
import { useTranslation } from "react-i18next";
import { IUMBStatusForDate } from "../../../main/interfaces/IUMBReservation";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { useNavigate } from "react-router-dom";

const localDate = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const Umbs: FC = () => {
  const [Umbs, setCadra] = useState<IUMB[][]>([]);
  const [cols, setCol] = useState<IUMB[]>([]);
  const [selectedUmb, setCader] = useState<IUMB>();
  const [modalState, setModalState] = useState(false);
  const [isSwitchMode, setIsSwitchMode] = useState(false);
  const [firstUmbrella, setFirstUmbrella] = useState<IUMB | null>(null);
  const [secondUmbrella, setSecondUmbrella] = useState<IUMB | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [areas, setAreas] = useState<IArea[]>([]);
  const [selectedArea, setSelectedArea] = useState<number>(0);
  const [reservedToday, setReservedToday] = useState<
    Map<number, IUMBStatusForDate>
  >(new Map());
  const [checkingInReservation, setCheckingInReservation] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ISwitchRequest>({
    defaultValues: {
      switch: "",
    },
  });

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const user = useGetUser();

  const getUmbs = () => {
    return axiosInstance.get(ApiConstants.umb.list).then((data) => {
      const sortedData = data.data.sort(
        (a: IUMB, b: IUMB) => (a.rowId ?? 0) - (b.rowId ?? 0),
      );

      const groupedData = sortedData.reduce(
        (entryMap: Map<number, IUMB[]>, e: IUMB) =>
          entryMap.set(e.rowId ?? 0, [
            ...(entryMap.get(e.rowId ?? 0) || []),
            e,
          ]),
        new Map<number, IUMB[]>(),
      );

      const dataFormated: IUMB[][] = [];
      for (const items of groupedData.values()) {
        dataFormated.push(items);
      }

      setCadra(dataFormated);

      const datas = [...dataFormated];
      if (datas.length > 0) {
        setCol(datas.sort().pop() ?? []);
      } else {
        setCol([]);
      }
    });
  };

  const getAreas = () => {
    axiosInstance.get(ApiConstants.area.list).then((res) => setAreas(res.data));
  };

  const getReservedToday = () => {
    return axiosInstance
      .post<IUMBStatusForDate[]>(ApiConstants.umbReservation.statusForDate, {
        serviceDate: localDate(),
        areaId: null,
      })
      .then((response) => {
        setReservedToday(
          new Map((response.data ?? []).map((item) => [item.umbId, item])),
        );
      });
  };

  const reloadUmbrellaState = () =>
    Promise.all([getUmbs(), getReservedToday()]);

  useEffect(() => {
    getUmbs();
    getAreas();
    getReservedToday();
  }, []);

  function formatPrice(umb: IUMB | null | undefined) {
    if (!umb) return;

    if (umb.secondPriceTime) {
      const nowHour = new Date().getHours();
      const secondHour = new Date(umb.secondPriceTime).getHours();
      if (nowHour >= secondHour) {
        return umb.secondPrice;
      }
    }

    return umb.firstPrice;
  }

  const approvo = (umb: IUMB | null | undefined) => {
    if (!umb) return;

    const request = {
      id: umb.id,
      isBusy: !umb.isBusy,
      price: Number(formatPrice(umb)),
      userId: user?.id ?? 0,
    };

    axiosInstance.post(ApiConstants.umb.approve, request).then(() => {
      reloadUmbrellaState();
      setModalState(false);
    });
  };

  const checkInReservation = () => {
    if (!selectedUmb?.id) return;
    const reservation = reservedToday.get(selectedUmb.id);
    if (!reservation) return;

    setCheckingInReservation(true);
    axiosInstance
      .post(ApiConstants.umbReservation.checkIn, {
        reservationId: reservation.reservationId,
        serviceDate: localDate(),
        umbReservationItemIds: [reservation.umbReservationItemId],
      })
      .then(() => {
        toast.success(i18n.t("umbReservations.messages.checkedIn"));
        setModalState(false);
        return reloadUmbrellaState();
      })
      .finally(() => setCheckingInReservation(false));
  };

  const switchUmbrellas = () => {
    if (!firstUmbrella || !secondUmbrella) return;

    axiosInstance
      .post(ApiConstants.umb.switch, {
        currentUMBId: firstUmbrella.id,
        newUMBId: secondUmbrella.id,
        switch: control?._formValues?.switch,
      })
      .then(() => {
        setIsModalOpen(false);
        setFirstUmbrella(null);
        setSecondUmbrella(null);
        setIsSwitchMode(false);
        reloadUmbrellaState();
      })
      .catch(() => {});
  };

  const handleUmbrellaSelect = (umb: IUMB) => {
    if (!firstUmbrella) {
      if (umb.isBusy) {
        return setFirstUmbrella(umb);
      }
      return toast.error(i18n.t("umb.selectOccupiedUmbrella"));
    }

    if (!secondUmbrella) {
      if (!umb.isBusy && !reservedToday.has(umb.id ?? 0)) {
        setSecondUmbrella(umb);
        setIsModalOpen(true);
        return;
      }
      if (reservedToday.has(umb.id ?? 0)) {
        return toast.error(i18n.t("umbReservations.errors.switchReserved"));
      }
      return toast.error(i18n.t("umb.selectFreeUmbrella"));
    }
  };

  const filteredUmbs = useMemo(() => {
    let source = Umbs;

    if (selectedArea !== 0) {
      source = source
        .map((row) => row.filter((umb) => umb.areaId === selectedArea))
        .filter((row) => row.length > 0);
    }

    if (!searchTerm.trim()) return source;

    const term = searchTerm.toLowerCase();
    return source
      .map((row) =>
        row.filter(
          (umb) =>
            umb.name?.toLowerCase().includes(term) ||
            umb.code?.toLowerCase().includes(term),
        ),
      )
      .filter((row) => row.length > 0);
  }, [Umbs, searchTerm, selectedArea]);

  const maxColumns = useMemo(() => {
    if (filteredUmbs.length === 0) return 0;
    return Math.max(...filteredUmbs.map((row) => row.length));
  }, [filteredUmbs]);

  const content = (
    <>
      {/* HEADER */}
      <div className="umb-header">
        <div className="umb-header-left">
          <div className="umb-header-actions">
            <select
              className="ui-control ui-select umb-area-select"
              value={selectedArea}
              onChange={(event) => setSelectedArea(Number(event.target.value))}
              aria-label={i18n.t("area.area")}
            >
              <option value={0}>{i18n.t("area.chooseArea")}</option>
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => navigate("/umb-reservations")}
              className="umb-reservations-btn"
              aria-label={i18n.t("umbReservations.title")}
              title={i18n.t("umbReservations.title")}
            >
              <EventAvailableIcon />
              <span>{i18n.t("umbReservations.reservationsButton")}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsSwitchMode(!isSwitchMode);
                setFirstUmbrella(null);
                setSecondUmbrella(null);
              }}
              className={`umb-switch-btn ${isSwitchMode ? "umb-switch-btn--active" : ""}`}
              aria-label={i18n.t("generic.switch")}
              aria-pressed={isSwitchMode}
            >
              <BeachAccessIcon />
            </button>
          </div>
        </div>

        <div className="umb-header-right">
          {/* STATS */}
          {/* <div className="umb-stats">
            <div className="umb-stat-item">
              <div className="umb-stat-label">{i18n.t(`umb.total`)}</div>
              <div className="umb-stat-value">{Umbs.flat().length}</div>
            </div>
            <div className="umb-stat-divider" />
            <div className="umb-stat-item">
              <div className="umb-stat-label">{i18n.t(`umb.occupied`)}</div>
              <div className="umb-stat-value umb-stat-value--danger">{Umbs.flat().filter((u) => u.isBusy).length}</div>
            </div>
            <div className="umb-stat-divider" />
            <div className="umb-stat-item">
              <div className="umb-stat-label"> {i18n.t('umb.available')}</div>
              <div className="umb-stat-value umb-stat-value--success">{Umbs.flat().filter((u) => !u.isBusy).length}</div>
            </div>
          </div> */}

          {/* SEARCH */}
          {/* <div className="umb-search">
            <input
              type="text"
              className={`umb-search-input ${
                searchFocused ? "umb-search-input--focused" : ""
              }`}
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            />
            <div className="umb-search-icon">🔍</div>

            {searchTerm && (
              <button
                type="button"
                className="umb-search-clear"
                onClick={() => setSearchTerm("")}
              >
                Clear
              </button>
            )}
          </div> */}
        </div>
      </div>

      {/* SWITCH MODE BANNER */}
      {/* {isSwitchMode && (
        <div className="umb-switch-banner">
          <div className="umb-switch-banner-left">
            <div className="umb-switch-badge">SWITCH MODE ACTIVE</div>
            <div className="umb-switch-info">
              {firstUmbrella ? (
                <div className="umb-switch-line">
                  <span className="umb-switch-label">Selected (occupied):</span>
                  <span className="umb-switch-chip umb-switch-chip--danger">
                    {firstUmbrella.name} ({firstUmbrella.code})
                  </span>
                </div>
              ) : (
                <div className="umb-switch-hint">
                  Click on an occupied umbrella first...
                </div>
              )}

              {secondUmbrella ? (
                <div className="umb-switch-line">
                  <span className="umb-switch-label">Selected (free):</span>
                  <span className="umb-switch-chip umb-switch-chip--success">
                    {secondUmbrella.name} ({secondUmbrella.code})
                  </span>
                </div>
              ) : (
                firstUmbrella && (
                  <div className="umb-switch-hint">
                    Now click on a free umbrella...
                  </div>
                )
              )}
            </div>
          </div>

          <button
            type="button"
            className="umb-switch-exit-btn"
            onClick={() => {
              setIsSwitchMode(false);
              setFirstUmbrella(null);
              setSecondUmbrella(null);
            }}
          >
            Exit Switch Mode
          </button>
        </div>
      )} */}

      {/* GRID WRAPPER */}
      <div className="umb-grid-wrapper">
        {filteredUmbs.length === 0 && !searchTerm && (
          <div className="umb-empty-state">
            <BeachAccessIcon className="umb-empty-icon" />
            <div>{i18n.t(`generic.noUmbrellasAvailable`)}</div>
          </div>
        )}

        {filteredUmbs.length === 0 && searchTerm && (
          <div className="umb-empty-state">
            {i18n.t(`generic.noUmbrellasFoundFor`)}{" "}
            <strong>"{searchTerm}"</strong>
          </div>
        )}

        {filteredUmbs.length > 0 && (
          <>
            <div className="umb-rows">
              {filteredUmbs.map((row, rowIndex) => (
                <div key={`row-${rowIndex}`} className="umb-row">
                  {row.map((cader: IUMB, colIndex: number) =>
                    (() => {
                      const isBusy = Boolean(cader.isBusy);
                      const isReserved =
                        !isBusy && reservedToday.has(cader.id ?? 0);
                      const stateClass = isBusy
                        ? "busy"
                        : isReserved
                          ? "reserved"
                          : "free";
                      const openCount =
                        "todayOpenCount" in cader
                          ? Number(cader.todayOpenCount ?? 0)
                          : 0;
                      return (
                        <div
                          key={`umb-${cader.id}`}
                          className={`umb-card umb-card--${stateClass} ${isSwitchMode ? "umb-card--clickable" : ""}`}
                          onClick={() => {
                            if (isSwitchMode) {
                              handleUmbrellaSelect(cader);
                            } else {
                              setModalState(true);
                              setCader(cader);
                            }
                          }}
                        >
                          <div className={`umb-card-name umb-card-name--${stateClass}`}>
                            {cader.name}
                          </div>

                          <div className="umb-card-main">
                            <BeachAccessIcon
                              className={`umb-card-icon umb-card-icon--${stateClass}`}
                            />
                            <div
                              className={`umb-card-status-dot umb-card-status-dot--${stateClass}`}
                              title={i18n.t("umb.openedToday", {
                                count: openCount,
                              })}
                              aria-label={i18n.t("umb.openedToday", {
                                count: openCount,
                              })}
                            >
                              {openCount}
                            </div>
                          </div>

                          <div className="umb-card-coords">
                            <span className="umb-card-coord-pill">
                              {rowIndex + 1}
                            </span>
                            <span className="umb-card-coord-pill">
                              {colIndex + 1}
                            </span>
                          </div>
                        </div>
                      );
                    })(),
                  )}

                  {/* empty placeholders */}
                  {row.length < maxColumns &&
                    Array.from({ length: maxColumns - row.length }).map(
                      (_, index) => (
                        <div
                          key={`empty-${rowIndex}-${index}`}
                          className="umb-card-placeholder"
                        />
                      ),
                    )}
                </div>
              ))}
            </div>

            {/* LEGEND */}
            <div className="umb-legend">
              <div className="umb-legend-items">
                <div className="umb-legend-item">
                  <div className="umb-legend-dot umb-legend-dot--busy" />
                  <span>
                    {i18n.t("umb.occupied")} (
                    {Umbs.flat().filter((u) => u.isBusy).length})
                  </span>
                </div>
                <div className="umb-legend-item">
                  <div className="umb-legend-dot umb-legend-dot--reserved" />
                  <span>
                    {i18n.t("umbReservations.reservedToday")} (
                    {
                      Umbs.flat().filter(
                        (u) => !u.isBusy && reservedToday.has(u.id ?? 0),
                      ).length
                    }
                    )
                  </span>
                </div>
                <div className="umb-legend-item">
                  <div className="umb-legend-dot umb-legend-dot--free" />
                  <span>
                    {i18n.t(`umb.available`)} (
                    {
                      Umbs.flat().filter(
                        (u) => !u.isBusy && !reservedToday.has(u.id ?? 0),
                      ).length
                    }
                    )
                  </span>
                </div>
              </div>

              <div className="umb-legend-subtext">
                {i18n.t(`generic.showing`)}{" "}
                <strong>{filteredUmbs.flat().length}</strong>{" "}
                {i18n.t(`generic.umbrella(s)`)}
                {searchTerm && ` for "${searchTerm}"`}
              </div>
            </div>
          </>
        )}
      </div>

      {/* SWITCH MODAL */}
      {isModalOpen && (
        <GeneralModal
          headerName={i18n.t("umb.confirmSwitch")}
          handleClose={() => {
            setIsModalOpen(false);
            setSecondUmbrella(null);
          }}
          handleClick={switchUmbrellas}
          width="400px"
        >
          <p>
            {i18n.t(`generic.areYouSureYouWantToSwitchUmbrella`)}{" "}
            <b>{firstUmbrella?.name}</b> {i18n.t(`generic.with`)}{" "}
            <b>{secondUmbrella?.name}</b>?
          </p>
          <GeneralInput
            label={i18n.t(`generic.switch`)}
            name="switch"
            type="text"
            placeholder={i18n.t(`generic.leaveANote`)}
            errors={errors}
            control={control}
          />
        </GeneralModal>
      )}

      {/* APPROVE MODAL */}
      {modalState &&
        (() => {
          const reservation = selectedUmb?.id
            ? reservedToday.get(selectedUmb.id)
            : undefined;
          const isReserved = Boolean(reservation) && !selectedUmb?.isBusy;
          return (
            <GeneralModal
              width="20vh"
              minwidth="400px"
              headerName={selectedUmb && selectedUmb.name}
              variant={
                isReserved
                  ? "warning"
                  : selectedUmb && selectedUmb.isBusy
                    ? "danger"
                    : "success"
              }
              handleClick={() =>
                isReserved ? checkInReservation() : approvo(selectedUmb)
              }
              handleClose={() => setModalState(false)}
            >
              <div className="styleRowFlex">
                <p>
                  <strong>{i18n.t(`generic.column`)} </strong>{" "}
                  {selectedUmb?.code}
                </p>
                <p>
                  <strong>{i18n.t(`generic.row`)} </strong>{" "}
                  {selectedUmb?.row?.name}
                </p>
              </div>
              <div className="styleRowFlex">
                <p>
                  <strong>{i18n.t(`generic.status`)} </strong>{" "}
                  {i18n.t(
                    selectedUmb?.isBusy
                      ? "umb.occupied"
                      : isReserved
                        ? "umbReservations.reservedToday"
                        : "umb.available",
                  )}
                </p>
                <p>
                  <strong>{i18n.t(`generic.price`)} </strong>{" "}
                  {Number(formatPrice(selectedUmb) || 0).toFixed(2)}
                </p>
              </div>
              {isReserved && reservation && (
                <div className="umb-reservation-info">
                  <p>
                    <strong>{i18n.t("umbReservations.customer")}:</strong>{" "}
                    {reservation.customerName}
                  </p>
                  <p>
                    <strong>{i18n.t("umbReservations.customerPhone")}:</strong>{" "}
                    {reservation.customerPhone}
                  </p>
                  {checkingInReservation && (
                    <p>{i18n.t("umbReservations.checkingIn")}</p>
                  )}
                </div>
              )}
            </GeneralModal>
          );
        })()}
    </>
  );
  return isFullscreen ? (
    <div className="umb-fullscreen-root">{content}</div>
  ) : (
    <BasicCli>{content}</BasicCli>
  );
};

export default Umbs;
